import { FC, lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { RoutePaths } from './RoutePaths';
import { ProtectedRoute } from 'components/pages/ProtectedRoute';
import { LoadingPage } from 'components/common/loading/LoadingPage';
import { Layout } from 'components/layout/Layout';
import { useApplicationContext } from 'components/hooks/UseApplicationContext';
import NotFound from 'components/pages/errors/NotFound';
import RedirectServicesPage from 'components/pages/UpdateBookmarkPage/RedirectServicesPage';

// Disclaimer
const DisclaimerLayout = lazy(() => import('components/pages/disclaimerPage/DisclaimerPage'));
const DisclaimerLastValidatedLayout = lazy(() => import('components/pages/disclaimerPage/DisclaimerLastValidated'));
const CountryRestrictedLayout = lazy(() => import('components/pages/disclaimerPage/CountryRestrictedLayout'));

// MyAccount
const MyAccountLayout = lazy(() => import('components/pages/myAccountPage/MyAccountLayout'));

// Notifications
const MyNotificationLayout = lazy(() => import('components/pages/notificationPage/NotificationList/MyNotificationLayout'));
const NotificationClickTrackingLayout = lazy(() => import('components/pages/notificationPage/Tracking/NotificationClickTrackingLayout'));

// ServiceBoard
const RedirectComponent = lazy(() => import('components/pages/serviceBoard/RedirectComponent'));
const MyServicesB2b2cLayout = lazy(() => import('components/pages/myServicesPage/MyServicesB2b2cLayout'));
const EssentialsLayout = lazy(() => import('components/pages/myServicesPage/layout/essentials/EssentialsLayout'));
const AllServicesLayout = lazy(() => import('components/pages/myServicesPage/layout/allservices/AllServicesLayout'));
const CatalogLayout = lazy(() => import('components/pages/catalogPage/CatalogLayout'));
const SearchLayout = lazy(() => import('components/pages/search/SearchLayout'));

export const AppRoutes: FC = () => {
    const { hasOnlyB2b2cAccess } = useApplicationContext();

    return <Layout>
        <Suspense fallback={<LoadingPage />}>
            <Routes>
                {/* Disclaimer Routes */}
                <Route
                    path={RoutePaths.Disclaimer.ByApplication.route}
                    element={<ProtectedRoute><DisclaimerLayout /></ProtectedRoute>}
                />
                <Route
                    path={RoutePaths.Disclaimer.Validated.route}
                    element={<ProtectedRoute><DisclaimerLastValidatedLayout /></ProtectedRoute>}
                />
                <Route
                    path={RoutePaths.Disclaimer.CountryRestricted.route}
                    element={<ProtectedRoute><CountryRestrictedLayout /></ProtectedRoute>}
                />

                {/* MyAccount Routes */}
                <Route
                    path={RoutePaths.MyAccount.route}
                    element={<ProtectedRoute><MyAccountLayout /></ProtectedRoute>}
                />

                {/* Notification Routes */}
                <Route
                    path={RoutePaths.MyNotification.route}
                    element={<ProtectedRoute><MyNotificationLayout /></ProtectedRoute>}
                />
                <Route
                    path={RoutePaths.Notification.route}
                    element={<ProtectedRoute><NotificationClickTrackingLayout /></ProtectedRoute>}
                />

                {/* ServiceBoard Routes */}
                {hasOnlyB2b2cAccess
                    && <Route
                        path={RoutePaths.Home.route}
                        element={<ProtectedRoute><MyServicesB2b2cLayout /></ProtectedRoute>} />
                }

                {!hasOnlyB2b2cAccess && <>
                    {/* MyServices Routes */}
                    <Route
                        path={RoutePaths.Home.route}
                        element={<ProtectedRoute><RedirectComponent /></ProtectedRoute>}
                    />
                    <Route
                        path={RoutePaths.MyServices.route}
                        element={<Navigate replace to={RoutePaths.MyServices.Essentials.url()} />}
                    />
                    <Route
                        path={RoutePaths.MyServices.Essentials.route}
                        element={<ProtectedRoute><EssentialsLayout /></ProtectedRoute>}
                    />
                    <Route
                        path={RoutePaths.MyServices.AllServices.route}
                        element={<ProtectedRoute><AllServicesLayout /></ProtectedRoute>}
                    />

                    {/* Catalog Routes */}
                    <Route
                        path={RoutePaths.Catalog.route}
                        element={<Navigate replace to={RoutePaths.Catalog.Browse.url()} />}
                    />
                    <Route
                        path={RoutePaths.Catalog.Browse.route}
                        element={<ProtectedRoute><CatalogLayout /></ProtectedRoute>}
                    />
                    <Route
                        path={RoutePaths.Catalog.ForYou.route}
                        element={<ProtectedRoute><CatalogLayout /></ProtectedRoute>}
                    />

                    {/* Search Routes */}
                    <Route
                        path={RoutePaths.Search.route}
                        element={<ProtectedRoute><SearchLayout /></ProtectedRoute>}
                    />
                </>}

                <Route path={RoutePaths.Services.route} element={<RedirectServicesPage />} />

                <Route path={RoutePaths.Errors.NotFound.route} element={<NotFound />} />
            </Routes>
        </Suspense>
    </Layout>;
};